// Configuration for home page routing
// Allows customization of the default landing page based on user email

export const HOME_PAGE_CONFIG = {
  // Default home page path for all users
  defaultHomePath: '/dashboard',
  
  // Whether to enable case-sensitive email matching (default: false)
  caseSensitive: false,
  
  // Whether to trim whitespace from emails before comparison (default: true)
  trimWhitespace: true,
  
  // Email-to-home-page mapping
  // Format: { 'email@example.com': '/path/to/home' }
  // Example: { 'admin@company.com': '/admin/dashboard', 'user@company.com': '/dashboard' }
  emailHomepageMap: {
    'michael@wordjuice.net': '/camp-series',
    'atiba@fitterandfaster.com': '/camp-series'
    // 'admin@example.com': '/admin/dashboard',
    // 'manager@example.com': '/reports',
  }
};

/**
 * Get the home page path for a specific user email
 * Falls back to default home page if user has no custom mapping
 * @param email - The user's email address
 * @returns string - The home page path for this user
 */
export function getHomePageForUser(email: string): string {
  if (!email) {
    return HOME_PAGE_CONFIG.defaultHomePath;
  }
  
  let normalizedEmail = email;
  
  // Apply trimming if configured
  if (HOME_PAGE_CONFIG.trimWhitespace) {
    normalizedEmail = normalizedEmail.trim();
  }
  
  // Apply case sensitivity if configured
  if (!HOME_PAGE_CONFIG.caseSensitive) {
    normalizedEmail = normalizedEmail.toLowerCase();
  }
  
  // Check if user has a custom home page mapping
  const customHomePath = findCustomHomePath(normalizedEmail);
  if (customHomePath) {
    return customHomePath;
  }
  
  // Fall back to default
  return HOME_PAGE_CONFIG.defaultHomePath;
}

/**
 * Find a custom home page for a user by checking the email map
 * @param normalizedEmail - The normalized email address
 * @returns string | null - The custom home page path or null if not found
 */
function findCustomHomePath(normalizedEmail: string): string | null {
  for (const email in HOME_PAGE_CONFIG.emailHomepageMap) {
    let mapEmail = email;
    
    // Apply same normalization to config emails
    if (HOME_PAGE_CONFIG.trimWhitespace) {
      mapEmail = mapEmail.trim();
    }
    
    if (!HOME_PAGE_CONFIG.caseSensitive) {
      mapEmail = mapEmail.toLowerCase();
    }
    
    if (mapEmail === normalizedEmail) {
      return HOME_PAGE_CONFIG.emailHomepageMap[email];
    }
  }
  
  return null;
}

/**
 * Add a custom home page for a user
 * @param email - The email address
 * @param homePath - The home page path
 */
export function setUserHomePage(email: string, homePath: string): void {
  if (email && homePath) {
    HOME_PAGE_CONFIG.emailHomepageMap[email.toLowerCase().trim()] = homePath;
  }
}

/**
 * Remove a custom home page for a user
 * @param email - The email address
 */
export function removeUserHomePage(email: string): void {
  if (!email) {
    return;
  }
  
  const normalizedEmail = email.toLowerCase().trim();
  
  for (const mapEmail in HOME_PAGE_CONFIG.emailHomepageMap) {
    let key = mapEmail;
    
    if (HOME_PAGE_CONFIG.trimWhitespace) {
      key = key.trim();
    }
    
    if (!HOME_PAGE_CONFIG.caseSensitive) {
      key = key.toLowerCase();
    }
    
    if (key === normalizedEmail) {
      delete HOME_PAGE_CONFIG.emailHomepageMap[mapEmail];
      break;
    }
  }
}
